/**
 * Continuous defect helpers. A continuous defect is an observation spanning more
 * than one keyframe, so it renders on every card in its range at its own position.
 */

/** Builds a lookup of keyframe id to its ordered position and display fields. */
export const buildKeyframeIndex = (keyframesData = []) => {
  const ordered = [...keyframesData].sort(
    (a, b) => Number(a?.sequenceNumber ?? 0) - Number(b?.sequenceNumber ?? 0),
  );
  const index = new Map();
  ordered.forEach((keyframe, order) => {
    index.set(keyframe.id, {
      order,
      id: keyframe.id,
      frameId: keyframe.sequenceNumber,
      chainage: keyframe.chainage,
      isHidden: keyframe.isHidden,
    });
  });
  return index;
};

/** Range metadata for one observation as seen from one keyframe, null if not continuous. */
export const buildContinuousMeta = (
  observationKeyframeIds = [],
  keyframeIndex,
  currentKeyframeId,
) => {
  if (!keyframeIndex || observationKeyframeIds.length < 2) return null;

  const ordered = observationKeyframeIds
    .map((id) => keyframeIndex.get(id))
    .filter(Boolean)
    .sort((a, b) => a.order - b.order);

  if (ordered.length < 2) return null;

  const position = ordered.findIndex((kf) => kf.id === currentKeyframeId);

  return {
    keyframeIds: ordered.map((kf) => kf.id),
    span: ordered.length,
    position: position >= 0 ? position + 1 : null,
    start: ordered[0],
    end: ordered[ordered.length - 1],
  };
};

/** Formats a chainage as "0.20m". */
export const formatChainage = (chainage) =>
  chainage == null ? "-" : `${Number(chainage).toFixed(2)}m`;

/** Formats a range end as "#3 (0.60m)". */
export const formatRangeEnd = (keyframe) =>
  keyframe
    ? `#${keyframe.frameId} (${formatChainage(keyframe.chainage)})`
    : "-";
